import { Box } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { GridCheckbox } from '~/components/GridCheckbox';
import { CardActionsTitleText } from '~/components/cards';
import {
  CardActions,
  CardActionsBottom,
  CardActionsTop,
  SelectableGridItem,
} from '~/components/cards/styles';
import { DeviceLogo, Screenshot } from '~/components/devices';
import { DeviceStatus, activationStatus, connectionStatus } from '~/components/devices/';
import { Link } from '~/components/link';
import { GridCard } from '~/components/media/card';
import { Spacer } from '~/components/spacer';
import { ago, formatDate } from '~/lib/datetime';
import type { DeviceList__Device as Device } from '../../queries/list.generated';
import { DeviceInfo, OverlayContainer, ThumbnailContainer } from '../lib';
import { Menu } from './';

interface DeviceGridCardProps {
  device: Device;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
  selected: boolean;
}

const DeviceGridCard = ({ device, onCheck, selectedIds, selected }: DeviceGridCardProps) => {
  const { t, i18n } = useTranslation();
  const lastConnected = device.timeSinceUpdate
    ? formatDate(ago(device.timeSinceUpdate), {
        format: {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          weekday: 'short',
        },
      })
    : null;
  const { caption } = device.active
    ? connectionStatus(device, t, i18n.language)
    : activationStatus(device, t);

  return (
    <>
      {/* TODO: don't show for content editor, needs fix */}
      {device.canUpdate.value && (
        <GridCheckbox
          checked={selected}
          inputProps={{ 'aria-label': t('common.actions.select_item', { name: device.name }) }}
          onChange={(event) => {
            if (event.target.checked) {
              return onCheck([...selectedIds, device.id]);
            }
            onCheck(selectedIds.filter((x) => x !== device.id));
          }}
        />
      )}
      <GridCard>
        <Link
          to={`${device.id}/details`}
          // The overlay's ID, IP and MAC would otherwise make up the link's name.
          aria-label={device.name}
          underline="none"
          sx={{ display: 'block' }}
        >
          <ThumbnailContainer>
            <Screenshot pollInterval={60_000} screenshotUri={device.screenshotUri} />
            <OverlayContainer className="overlay-section">
              <Spacer />
              <DeviceInfo>
                {device.id}
                <br />
                {device.ipAddress}
                <br />
                {device.macAddress}
                <br />
                {device.playerVersion}
              </DeviceInfo>
            </OverlayContainer>
          </ThumbnailContainer>
        </Link>
        <CardActions>
          <CardActionsTop>
            <Box sx={{ maxWidth: '30px', overflow: 'hidden', mr: 1 }}>
              <DeviceLogo kind={device.kind} />
            </Box>
            <CardActionsTitleText title={device.name} />
          </CardActionsTop>
          <CardActionsBottom>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DeviceStatus device={device} />
              <div>•</div>
              <div>{lastConnected ?? caption}</div>
            </Box>
            {/* TODO: don't show for content editor, needs fix */}
            {device.canUpdate.value && <Menu device={device} />}
          </CardActionsBottom>
        </CardActions>
      </GridCard>
    </>
  );
};

interface GridRowProps {
  item: Device;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}

export const GridRow = ({ item, onCheck, selectedIds }: GridRowProps) => {
  const selected = selectedIds.includes(item.id);

  return (
    <SelectableGridItem item xs={12} sm={6} md={3} lg={2} key={item.id} selected={selected}>
      <DeviceGridCard
        device={item}
        onCheck={onCheck}
        selectedIds={selectedIds}
        selected={selected}
      />
    </SelectableGridItem>
  );
};
